import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Input from "@/components/common/inputs/Input";
import Button from "@/components/common/Button";
import { useLoader } from "@/hooks/LoaderProvider";
import { ArrowRightOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import { useDispatch, useSelector } from "react-redux";
import {
  resetPasswordAsync,
  selectPasswordResetUserID,
} from "@/store/redux/slices/auth/auth_slice";
import WebIcon from "@/components/common/Web_Icons";
import AuthHeader from "@/components/layout/Header/Auth_Header";

const PasswordReset = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Prefer navigate state; fall back to Redux if the user refreshed the reset page.
  const passwordResetUserID = useSelector(selectPasswordResetUserID);
  const userID = location.state?.userID || passwordResetUserID;

  const handlePasswordChange = (e) => setPassword(e.target.value);
  const handleConfirmPasswordChange = (e) => setConfirmPassword(e.target.value);
  const handleCodeChange = (e) => setCode(e.target.value);

  const handleProceed = async () => {
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match. Please try again.");
      setIsErrorVisible(true);
      return;
    }

    if (!code || !password || !confirmPassword) {
      setErrorMessage("Please fill in all required fields.");
      setIsErrorVisible(true);
      return;
    }

    if (!userID) {
      setErrorMessage(
        "Missing user information. Please request a new recovery code."
      );
      setIsErrorVisible(true);
      return;
    }

    showLoader();
    try {
      const resultAction = await dispatch(
        resetPasswordAsync({
          code,
          newPassword: password,
          userID,
        })
      );

      if (resetPasswordAsync.fulfilled.match(resultAction)) {
        setIsSuccessVisible(true);
      } else {
        setErrorMessage(
          resultAction.payload || "Failed to reset password. Please try again."
        );
        setIsErrorVisible(true);
      }
    } catch {
      setErrorMessage(
        "An error occurred while resetting your password. Please try again."
      );
      setIsErrorVisible(true);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
    navigate("/signin");
  };

  const handleCloseError = () => {
    setIsErrorVisible(false);
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Top section */}
      <AuthHeader
        logoName="CMSLogo"
        logoSize="large"
        title="Have an existing Veegil SSO account?"
        titlePath="/signin"
        justifyBetween={true}
        className="px-4 py-2 md:px-6"
      />

      {/* Middle section */}
      <div className="flex flex-col px-4 md:px-6 max-w-md mx-auto w-full mt-8">
        <h1 className="heading-large whitespace-nowrap text-left text-primary mb-4">
          Password Reset
        </h1>
        <p className="text-small text-left text-darkGrey mb-6 w-full">
          Reset your password below and ensure it&apos;s kept save for easy
          access next time you want to access your Veegil account.
        </p>

        <div className="mb-4">
          <Input
            label="VERIFICATION CODE"
            placeholder="Enter the code sent to your email"
            icon={<WebIcon icon="Lock" size="small" />}
            type="number"
            value={code}
            onChange={handleCodeChange}
            id="code"
          />
        </div>

        <div className="mb-4">
          <Input
            label="NEW PASSWORD"
            placeholder="Enter your password"
            icon={<WebIcon icon="Lock" size="small" />}
            type="password"
            value={password}
            onChange={handlePasswordChange}
            id="password"
          />
        </div>

        <div className="mb-4">
          <Input
            label="CONFIRM PASSWORD"
            placeholder="Enter your password"
            icon={<WebIcon icon="Lock" size="small" />}
            type="password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            id="confirmPassword"
          />
        </div>

        <Button
          text="Proceed"
          icon={<ArrowRightOutlined />}
          onClick={handleProceed}
          isActive={password && confirmPassword && code}
          color="darkBlue"
          className={`w-full ${
            !password || !confirmPassword || !code
              ? "bg-darkBlue text-primary"
              : "bg-primary text-white"
          }`}
        />

        <div className="mt-8">
          <Copyright />
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="Password Reset Successful"
        paragraph="Your password has been successfully reset. You can now sign in with your new password."
        buttonText="Continue to Sign In"
        recoveryPath="/signin"
      />

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header="Password Reset Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
        recoveryPath="/password-reset"
      />
    </div>
  );
};

export default PasswordReset;
